import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabase';
import {
  CogIcon,
  CalculatorIcon,
  CurrencyDollarIcon,
  CheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    profit_margin: 70, // %70 kar oranı varsayılan
    set_discount_rate: 5, // %5 takım indirimi varsayılan
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // Settings tablosundan ayarları çek
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116: No rows found
        console.error('Settings yükleme hatası:', error);
        return;
      }

      if (data) {
        setSettings({
          profit_margin: parseFloat(data.profit_margin) || 70,
          set_discount_rate: parseFloat(data.set_discount_rate) || 5,
        });
      }
    } catch (error) {
      console.error('Settings yükleme hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Önce mevcut ayar var mı kontrol et
      const { data: existingSettings } = await supabase
        .from('settings')
        .select('id')
        .limit(1)
        .single();

      let result;
      if (existingSettings) {
        // Güncelle
        result = await supabase
          .from('settings')
          .update({
            profit_margin: settings.profit_margin,
            set_discount_rate: settings.set_discount_rate,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingSettings.id);
      } else {
        // Yeni oluştur
        result = await supabase
          .from('settings')
          .insert({
            profit_margin: settings.profit_margin,
            set_discount_rate: settings.set_discount_rate,
          });
      }

      if (result.error) {
        throw result.error;
      }

      setMessage({
        type: 'success',
        text: 'Ayarlar başarıyla kaydedildi!'
      });

      // 3 saniye sonra mesajı temizle
      setTimeout(() => setMessage(null), 3000);

    } catch (error) {
      console.error('Settings kaydetme hatası:', error);
      setMessage({
        type: 'error',
        text: 'Ayarlar kaydedilirken bir hata oluştu.'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    // Sadece pozitif sayılara izin ver
    const numValue = Math.max(0, parseFloat(value) || 0);
    setSettings(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Genel Ayarlar
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Fiyatlandırma ve indirim oranlarını yönetin
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`rounded-md p-4 ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex">
            <div className="flex-shrink-0">
              {message.type === 'success' ? (
                <CheckIcon className="h-5 w-5 text-green-400" />
              ) : (
                <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
              )}
            </div>
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                message.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {message.text}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Form */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <CogIcon className="h-5 w-5 mr-2 text-gray-500" />
            Fiyatlandırma Ayarları
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            Bu ayarlar tüm ürün fiyatlarını etkiler
          </p>
        </div>

        <div className="px-6 py-6 space-y-6">
          {/* Kar Oranı */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-900 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2 text-green-500" />
              Kar Oranı (%)
            </label>
            <div className="space-y-2">
              <input
                type="number"
                min="0"
                max="1000"
                step="0.1"
                value={settings.profit_margin}
                onChange={(e) => handleInputChange('profit_margin', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="70"
              />
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-2">💡 Kar Oranı Nasıl Çalışır?</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Admin panelinde girilen fiyatlar = Toptancı alım fiyatları</li>
                    <li>• Müşterilere gösterilen fiyat = Alım fiyatı + Kar oranı</li>
                    <li>• Örnek: Alım fiyatı 100₺, Kar oranı %70 → Satış fiyatı 170₺</li>
                    <li>• Tüm indirimler bu hesaplanmış fiyat üzerinden yapılır</li>
                  </ul>
                </div>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <p className="text-sm text-gray-700">
                  <strong>Örnek Hesaplama:</strong> 
                  <br />
                  Alım Fiyatı: 1.000₺ → Satış Fiyatı: {(1000 * (1 + settings.profit_margin / 100)).toLocaleString()}₺
                  <span className="text-green-600"> ({settings.profit_margin}% kar)</span>
                </p>
              </div>
            </div>
          </div>

          {/* Takım İndirim Oranı */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-900 flex items-center">
              <CalculatorIcon className="h-5 w-5 mr-2 text-purple-500" />
              Takım İndirim Oranı (%)
            </label>
            <div className="space-y-2">
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={settings.set_discount_rate}
                onChange={(e) => handleInputChange('set_discount_rate', e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="5"
              />
              <div className="bg-purple-50 border border-purple-200 rounded-md p-4">
                <div className="text-sm text-purple-800">
                  <p className="font-medium mb-2">🛋️ Takım İndirimi Nasıl Çalışır?</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Müşteri takım ürünlerinin tüm elemanlarından minimum adet alırsa bu indirim uygulanır</li>
                    <li>• İndirim kar oranı eklenmiş satış fiyatı üzerinden hesaplanır</li>
                    <li>• Örnek: Takım toplam 1.000₺, %{settings.set_discount_rate} indirim → Son fiyat {(1000 * (1 - settings.set_discount_rate / 100)).toLocaleString()}₺</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex justify-end">
            <button
              onClick={saveSettings}
              disabled={saving}
              className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <CheckIcon className="h-4 w-4 mr-2" />
                  Ayarları Kaydet
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Impact Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-amber-800">
              Önemli Uyarı
            </h3>
            <div className="mt-2 text-sm text-amber-700">
              <p>
                Bu ayarları değiştirmek tüm ürün fiyatlarını anında etkiler. 
                Değişiklik yapmadan önce mevcut siparişleri ve kampanyaları kontrol edin.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage; 